import React, {useMemo} from 'react'
import {Box, useSlotRecipe} from '@chakra-ui/react3'
import {useIntl} from 'react-intl'
import Link from '../../../link'
import CountrySelectorLink from '../../../country-selector-link'
import {CUSTOMER_SERVICE_HREF, STORE_LOCATOR_HREF} from '../../../../../constants'
import {flyoutToolNavRecipe} from './flyout-tool-nav.recipe'

/**
 * Tool navigation for the flyout: the secondary links and the country selector
 * (STOK-17026 §5).
 *
 * Functionally a row below the navigation tree, and last in the DOM so it also
 * comes last in the tab order — the spec puts these controls after every
 * navigation element. Where the row sits on screen is not decided here: the
 * placement slot in `flyout-navigation.recipe.js` owns that, including the
 * desktop overlap with the level-2 pane.
 *
 * The country selector links straight to the country-selection page rather than
 * offering the locale menu the legacy header carries — per spec its only
 * interaction is "open country selection page". It keeps that page's aria-label
 * so the control reads the same as before, which is why the visible
 * language/country pair is not the accessible name.
 *
 * @component
 * @returns {JSX.Element}
 */
const FlyoutToolNav = () => {
    const {formatMessage} = useIntl()
    const recipe = useSlotRecipe({recipe: flyoutToolNavRecipe})
    const styles = useMemo(() => recipe(), [recipe])

    return (
        <Box css={styles.root}>
            <Box css={styles.links}>
                {/* The label sits in a span of its own because the link recipe
                    hangs the underline off `& span`, so it tracks the text
                    rather than the whole control. `formatMessage` rather than
                    `FormattedMessage` for the same reason: the app configures
                    react-intl with `textComponent: 'span'`, which would nest a
                    second span inside this one and draw a second underline. */}
                <Link variant="secondary" size="small" to={CUSTOMER_SERVICE_HREF}>
                    <span>{formatMessage({id: 'global.help', defaultMessage: 'Help'})}</span>
                </Link>
                <Link variant="secondary" size="small" to={STORE_LOCATOR_HREF}>
                    <span>{formatMessage({id: 'global.stores', defaultMessage: 'Stores'})}</span>
                </Link>
            </Box>

            <CountrySelectorLink />
        </Box>
    )
}

if (process.env.NODE_ENV !== 'production') {
    FlyoutToolNav.displayName = 'FlyoutToolNav'
}

export default FlyoutToolNav
